import { err, ok, Result } from "neverthrow";
import { isEmpty } from "../utilities/isEmpty";
import { isSamoon } from "@/framework/utilities/isSamoon";
import {
  IKtznAuthenticationService,
  KtznAcceptLoginResponse,
  KtznAuthenticationService,
} from "@/modules/auth/services/ktznAuthenticationService";
import { ElectronService } from "@/framework/services/general/electronService";
import { ErrorService } from "@/framework/errors/ErrorService";
import { AuthService } from "@/framework/api/AuthService";
import { UserService } from "@/modules/auth/services/UserService";
import { CommonVuexStateRepository } from "@/framework/repositories/CommonVuexStateRepository";
import { AuthMutation } from "@/framework/store/auth/types";
import { i18n } from "@/framework/plugins/i18n";
import AuthenticationError from "@/modules/auth/errors/authenticationError";
import KtznAuthenticationError from "@/modules/auth/errors/ktznAuthenticationError";
import ErrorLogService, { IErrorLogService } from "@/framework/logger/errorLog/errorLogService";
import store from "@/store";
import router from "@/router/router";

/**
 * Kotozna auth token with an access token and a refresh token.
 */
export type KotoznaAuthToken = {
  /**
   * Kotozna access token string
   */
  accessToken: string;
  /**
   * Kotozna refresh token string
   */
  refreshToken: string;
};

export type LoginRequest = {
  has_sandbox: boolean;
  is_sandbox: boolean;
  is_session_active: boolean;
  message: string;
  success: boolean;
  transaction_code: string;
};

export type LoginError = {
  isError: boolean;
  errorMessage: string;
};

export type LoginAccept = {
  access_id: string;
  access_token_v2: string;
  id: string;
  refresh_token_v2: string;
  session_id: string;
  success: boolean;
  message: string;
  onetime_token: string;
  subscription_endpoint: string;
};

/**
 * Authentication Repository that manages the various Authentications
 * (Dreamily JWT Auth and KTZN Auth) supported by Kotozna.
 */
export interface IAuthenticationRepository {
  /**
   * Gets the JTW token string either from Cognito or Kotozna auth.
   * @returns Result type with either a token string or an AuthenticationError
   */
  getTokenString(): Promise<Result<string, AuthenticationError>>;

  /**
   * Gets the latest Kotozna auth token from the vuex store.
   * @returns Result type with either an KotoznaAuthToken or an AuthenticationError
   */
  getKtznAuthToken(): Result<KotoznaAuthToken, AuthenticationError>;

  /**
   * Calculates if a cognito token time is expired
   * @param tokenExp cognito token expire time in seconds
   * @returns true or false
   */
  isTokenExp(tokenExp: number): boolean;

  /**
   * Requesting a login to the Kotozna Auth server.
   *
   * - Entering the email
   * - Calls the REST API `/login/request`
   *
   * @param loginId User email
   * @param isSandBox If requesting to sandbox? (Default: `false`)
   * @param target ???
   * @returns Either a `LoginRequest` or a `LoginError`
   */
  requestLogin(loginId: string, isSandBox: boolean, target: string): Promise<Result<LoginRequest, LoginError>>;

  /**
   * Requesting to accept a login to the Kotozna Auth server.
   *
   * - Confirming the PIN that the user received via email
   * - Calls the REST API `/login/accept`
   *
   * @param transactionCode Transaction code from the previous response
   * @param confirmationCode Confirmation code from that the user received via email
   * @returns Either a `LoginAccept` or a `LoginError`
   */
  getLoginAcceptRes(transactionCode: string, confirmationCode: string): Promise<Result<LoginAccept, LoginError>>;

  /**
   * Requesting to accept a login to the Kotozna Auth server.
   *
   * - Confirming the onetime token that the user received via loginAccept API
   * - Calls the REST API `/login/accept_onetime`
   *
   * @param token Onetime token from the previous response
   * @param isSandbox true if you sign in as a sandbox user
   * @returns Either a `KtznAcceptLoginResponse` or a `KtznAuthenticationError`
   */
  getLoginAcceptResOnetime(token: string, isSandbox: boolean): Promise<Result<LoginAccept, LoginError>>;

  /**
   * Handles the auth refresh that is required for Kotozna Auth.
   *
   * Calling this method is not needed for Cognito because the AWS SDK handles it by itself.
   * @returns Result with either a empty `success` or a `LoginError`
   */
  authRefresh(): Promise<Result<null, LoginError>>;
}

/**
 * Authentication Repository that manages the various Authentications
 * (AWS Cognito and KTZN Auth) supported by Kotozna.
 */
export class AuthenticationRepository implements IAuthenticationRepository {
  private _ktznAuthenticationService: IKtznAuthenticationService;
  private _errorLogService: IErrorLogService;
  private _vuexStateRepository: CommonVuexStateRepository;
  private readonly _isElectron: Boolean;

  constructor(
    ktznAuthenticationService: IKtznAuthenticationService = new KtznAuthenticationService(),
    errorLogService: IErrorLogService = ErrorLogService,
    vuexStateRepository: CommonVuexStateRepository = new CommonVuexStateRepository()
  ) {
    this._ktznAuthenticationService = ktznAuthenticationService;
    this._errorLogService = errorLogService;
    this._vuexStateRepository = vuexStateRepository;
    this._isElectron = navigator.userAgent.indexOf(" Electron/") > -1;
  }

  /**
   * Gets the JTW token string either from Cognito or Kotozna auth.
   * @returns Result type with either a token string or an AuthenticationError
   */
  getTokenString = async (): Promise<Result<string, AuthenticationError>> => {
    // The `await` is needed even if `getKtznAuthToken` doesn't return a `Promise`
    const ktznRes = await this.getKtznAuthToken();
    if (ktznRes.isOk()) return ok(ktznRes.value.accessToken);
    else return err(new AuthenticationError(AuthenticationError.Type.noKtznAccessToken));
  };

  /**
   * Gets the latest Kotozna auth token from the vuex store.
   * @returns Result type with either an KotoznaAuthToken or an AuthenticationError
   */
  getKtznAuthToken = (): Result<KotoznaAuthToken, AuthenticationError> => {
    let authToken: KotoznaAuthToken = {
      accessToken: "",
      refreshToken: "",
    };
    if (store && store.getters && store.getters.accessToken && store.getters.accessToken.length !== 0) {
      authToken.accessToken = store.getters.accessToken;
    } else {
      return err(new AuthenticationError(AuthenticationError.Type.noKtznAccessToken));
    }
    if (store && store.getters && store.getters.refreshToken && store.getters.refreshToken.length !== 0) {
      authToken.refreshToken = store.getters.refreshToken;
    } else {
      return err(new AuthenticationError(AuthenticationError.Type.noKtznRefreshToken));
    }
    return ok(authToken);
  };

  /**
   * Calculates if a token time is expired.
   * @param tokenExp cognito token expire time in seconds
   * @returns true or false
   */
  isTokenExp = (tokenExp: number): boolean => {
    const currentUnixTime = Math.floor(Date.now() / 1000);
    return !(tokenExp && tokenExp > currentUnixTime);
  };

  /**
   * Requesting a login to the Kotozna Auth server.
   *
   * - Entering the email
   * - Calls the REST API `/login/request`
   *
   * @param loginId User email
   * @param isSandBox If requesting to sandbox? (Default: `false`)
   * @param target ???
   * @returns Either a `LoginRequest` or a `LoginError`
   */
  requestLogin = async (
    loginId: string,
    isSandBox: boolean = false,
    target: string = ""
  ): Promise<Result<LoginRequest, LoginError>> => {
    const res = await this._ktznAuthenticationService.loginRequest(loginId, isSandBox, target);
    if (res.isOk()) {
      return ok({
        has_sandbox: res.value.has_sandbox,
        is_sandbox: res.value.is_sandbox,
        is_session_active: res.value.is_session_active,
        message: res.value.message,
        success: res.value.success,
        transaction_code: res.value.transaction_code,
      });
    } else {
      console.error("Error occurred while requesting login");

      // Send error log
      let errorStack = res.error.stack || res.error.message;
      await this._errorLogService.sendAuthLog(
        "Ktzn.Auth.requestLogin",
        "loginRequest",
        { loginId, isSandBox, target },
        errorStack
      );

      const errorMessage =
        res.error.errorData && res.error.errorData.message ? res.error.errorData.message : JSON.stringify(res.error);
      return err({ isError: true, errorMessage: errorMessage });
    }
  };

  /**
   * Requesting to accept a login to the Kotozna Auth server.
   *
   * - Confirming the PIN that the user received via email
   * - Calls the REST API `/login/accept`
   *
   * @param transactionCode Transaction code from the previous response
   * @param confirmationCode Confirmation code from that the user received via email
   * @returns Either a `LoginAccept` or a `LoginError`
   */
  getLoginAcceptRes = async (
    transactionCode: string,
    confirmationCode: string
  ): Promise<Result<LoginAccept, LoginError>> => {
    const res = await this._ktznAuthenticationService.loginAccept(transactionCode, confirmationCode);
    return await this._doLoginAcceptRes(res, { transactionCode, confirmationCode });
  };

  /**
   * Requesting to accept a login to the Kotozna Auth server.
   *
   * - Confirming the onetime token that the user received via loginAccept API
   * - Calls the REST API `/login/accept_onetime`
   *
   * @param token Onetime token from the previous response
   * @param isSandbox true if you sign in as a sandbox user
   * @returns Either a `KtznAcceptLoginResponse` or a `KtznAuthenticationError`
   */
  getLoginAcceptResOnetime = async (token: string, isSandbox: boolean): Promise<Result<LoginAccept, LoginError>> => {
    const res = await this._ktznAuthenticationService.loginAcceptOnetimeToken(token, isSandbox);
    return await this._doLoginAcceptRes(res, { token });
  };

  /**
   * Handles the auth refresh that is required for Kotozna Auth.
   *
   * Calling this method is not needed for Cognito because the AWS SDK handles it by itself.
   * @returns Result with either a empty `success` or a `LoginError`
   */
  authRefresh = async (): Promise<Result<null, LoginError>> => {
    const refreshToken: string = store.getters.refreshToken;
    const res = await this._ktznAuthenticationService.authRefresh(refreshToken);
    if (res.isOk()) {
      const authRefreshRes = res.value;
      if (authRefreshRes.message === "success" && authRefreshRes.access_token && authRefreshRes.refresh_token) {
        store.commit(AuthMutation.SET_ACCESS_TOKEN, authRefreshRes.access_token);
        store.commit(AuthMutation.SET_REFRESH_TOKEN, authRefreshRes.refresh_token);
        store.commit(AuthMutation.SET_IS_USER_LOGGED_OUT, false);
      } else {
        console.error("Could not refresh new auth token");
        await this.logoutUserForcefully(false);
      }

      return ok(null);
    } else {
      // Send error log if Kotozna Auth refresh REST call fails and logout user
      await this.logoutUserForcefully(false, res.error.message);
      // Let's add but not required.
      return err({ isError: true, errorMessage: res.error.message });
    }
  };

  // Sign out Staff or Admin
  signOutUser = async (isAdmin: boolean, isSandbox: boolean = false) => {
    try {
      if (!store.getters.isUserLoggedOut && !isSandbox) {
        try {
          await new AuthService().beforeUserLogout("");
        } catch (e) {
          await new ErrorService().handleError(e);
        }
      }

      // Notify electron app about logout
      const electronService = new ElectronService();
      const res = await electronService.sendLogoutEvent();
      if (res.isErr()) {
        console.log(res.error);
      }

      // Redirect based on user role
      await this.redirectUser(isAdmin);
    } catch (e) {
      console.error(e);
      await this.logoutUserForcefully(isAdmin);
    }
  };

  signOutBeforeLogin = async () => {
    try {
      await this._vuexStateRepository.stopWebSocket();
    } catch (e) {
      console.error(e);
      await this.logoutUserForcefully(false);
    }
  };

  logoutUserForcefully = async (isAdmin: boolean, errorMessage: string = ""): Promise<void> => {
    try {
      console.warn("Logout user forcefully: Something went wrong");
      this._vuexStateRepository.clearAuthStore();
    } catch (e) {
      console.error(e);
    } finally {
      const targetPath = isAdmin ? `/${i18n.locale}/signin/admin` : `/${i18n.locale}/signin`;
      const currentPath = router.currentRoute.path;
      // Only navigate if not already on the target signin page
      if (currentPath !== targetPath) {
        router.push(targetPath).catch(() => {});
      }
    }
  };

  private _doLoginAcceptRes = async (
    res: Result<KtznAcceptLoginResponse, KtznAuthenticationError>,
    params: {
      token?: string;
      transactionCode?: string;
      confirmationCode?: string;
    }
  ): Promise<Result<LoginAccept, LoginError>> => {
    const variables = () => {
      if (params.token) return { token: params.token };
      else return { transactionCode: params.transactionCode, confirmationCode: params.confirmationCode };
    };
    if (res.isOk()) {
      return ok({
        access_id: res.value.access_id,
        access_token_v2: res.value.access_token_v2,
        id: res.value.id,
        refresh_token_v2: res.value.refresh_token_v2,
        session_id: res.value.session_id,
        success: res.value.success,
        message: res.value.message,
        onetime_token: res.value.onetime_token,
        subscription_endpoint: res.value.subscription_endpoint,
      });
    } else {
      // Send error log
      let errorStack = res.error.stack || res.error.message;
      console.error(`Error occurred in getLoginAcceptRes: ${errorStack}`);
      const logRes = await this._errorLogService.sendAuthLog(
        "Ktzn.Auth.getLoginAcceptRes",
        "loginAccept",
        variables,
        errorStack
      );
      if (logRes && logRes.isErr()) {
        console.log("getLoginAcceptRes failed: ", logRes.error.message);
      }
      const errorMessage =
        res.error.errorData && res.error.errorData.message ? res.error.errorData.message : res.error.message;
      return err({ isError: true, errorMessage: errorMessage });
    }
  };

  private async redirectUser(isAdmin: boolean) {
    if (isAdmin) {
      const user = await new UserService().getCachedQcUser();
      if (user.isErr()) {
        console.error("Error occurred while getting user");
        await this.logoutUserForcefully(true);
        return;
      }

      let srcUserType = "normal";
      if (!isEmpty(user.value) && user.value.sandboxType === "sandbox") {
        const res = await new AuthService().getSrcType();
        if (res.isErr()) {
          console.error("Error occurred while getting srcUserType");
          await this.logoutUserForcefully(true);
          return;
        }

        srcUserType = res.value && res.value.type ? res.value.type : "normal";
      }

      const staff = await new UserService().getCachedStaff();
      if (staff.isErr()) {
        console.error("Error occurred while getting staff");
        await this.logoutUserForcefully(true);
        return;
      }

      // Stop websocket connection
      await this._vuexStateRepository.stopWebSocket();

      // Clear stores
      this._vuexStateRepository.clearAuthStore();

      if (staff.value.myself.role === "master" || srcUserType === "master") {
        if (isSamoon()) {
          location.href = `${process.env.VUE_APP_KTZN_BH_FE_ADMIN_DOMAIN}/${i18n.locale}/tenant/list`;
        } else {
          location.href = `${process.env.VUE_APP_KTZN_FE_ADMIN_DOMAIN}/${i18n.locale}/tenant/list`;
        }
      } else if (staff.value.myself.role === "agency" || srcUserType === "agency") {
        location.href = `${process.env.VUE_APP_KTZN_FE_AGENCY_DOMAIN}/${i18n.locale}/tenant/list`;
      } else if (!router.currentRoute.path.includes("signin")) {
        router.push(`/${i18n.locale}/signin/admin`).catch(() => {});
      }
    } else {
      // Stop websocket connection
      await this._vuexStateRepository.stopWebSocket();

      // Clear stores
      this._vuexStateRepository.clearAuthStore();
      if (!router.currentRoute.path.includes("signin")) router.push(`/${i18n.locale}/signin`).catch(() => {});
    }
  }
}

export default new AuthenticationRepository();
